import { Trans } from "@lingui/react/macro";
import React, { useCallback } from "react";
import * as styles from "./ExpeditionBox.module.scss";
import {
  Button,
  Icon,
  InclusionContent,
  InclusionDrawerSection,
  InclusionsDrawerContent,
  InclusionsDrawerHeader,
  InclusionsDrawerFooterCta,
  useDrawer
} from "@silversea-ssc/ui-components-react";
import {
  ExpeditionInclusionModalContentFragment,
  ExpeditionInclusionModalItemContentFragment
} from "types/graphql-types";
import { IconsId } from "@silversea-ssc/design-system-icons";
import { isPresent } from "src/utils/checks";

const InclusionsDrawer = React.lazy(
  () => import("components/fare/InclusionsDrawer/InclusionsDrawer")
);

type Props = {
  content: ExpeditionInclusionModalContentFragment | undefined;
};

const ExpeditionBox: React.FC<Props> = ({ content }) => {
  const {
    closeDrawer,
    drawerData,
    isDrawerMountable,
    isDrowerOpen,
    openDrawer
  } = useDrawer<ExpeditionInclusionModalContentFragment>(undefined);

  const inclusions =
    drawerData?.inclusions?.map(toInclusionContent).filter(isPresent) || [];
  const onExpeditionBoxClick = useCallback(() => {
    if (content) openDrawer(content);
  }, [content, openDrawer]);

  if (!content) return null;

  return (
    <div className={styles.container}>
      <Icon name="routes-2" padding={false} size="2x" />
      <div>
        <Trans id="expedition-box-content">
          All our{" "}
          <Button
            variant="inline-text"
            onClick={onExpeditionBoxClick}
            dataContentPiece="expedition_inclusions_button"
          >
            EXPEDITION VOYAGES
          </Button>
          , the cruises traveling to the most remote regions, include in-country
          air, hotel, shore excursions and expedition gear, no matter which fare
          you’ll choose.
        </Trans>
      </div>
      {isDrawerMountable && drawerData && (
        <React.Suspense>
          <InclusionsDrawer
            title={drawerData.title?.localized}
            isOpen={isDrowerOpen}
            expeditionInfo={drawerData}
            onRequestClose={closeDrawer}
          >
            <InclusionsDrawerContent>
              <InclusionsDrawerHeader
                title={drawerData.title?.localized}
                description={drawerData.description?.localized}
                icon="routes"
              />
              {inclusions.map((inclusion) => (
                <InclusionDrawerSection
                  description={inclusion.description}
                  icon={inclusion.icon}
                  title={inclusion.title}
                  key={inclusion._id}
                />
              ))}
              <InclusionsDrawerFooterCta
                ctaLabel={
                  <Trans id="expedition-modal-link-cta-label">
                    Learn more about expedition cruises
                  </Trans>
                }
                fullPath={drawerData.link?.fullPath}
              />
            </InclusionsDrawerContent>
          </InclusionsDrawer>
        </React.Suspense>
      )}
    </div>
  );
};

const toInclusionContent = (
  content: ExpeditionInclusionModalItemContentFragment | undefined
): InclusionContent | null => {
  if (!content) return null;

  return {
    _id: content._key!,
    description: content.description?.localized || "",
    icon: content.icon as IconsId,
    title: content.title?.localized || "",
    codes: [],
    infographicTitle: content.title?.localized || ""
  };
};

export default ExpeditionBox;
